import { useCallback, useEffect, useRef, type RefObject } from "react"

function adjustHeight(textArea: HTMLTextAreaElement, initialHeightRef: RefObject<number | null>) {
  // We need to reset the height momentarily to get the correct scrollHeight for the textarea
  textArea.style.height = "0px"
  const scrollHeight = textArea.scrollHeight

  if (initialHeightRef.current == null) {
    const initialValue = textArea.value
    textArea.value = "&nbsp;"

    initialHeightRef.current = textArea.scrollHeight

    textArea.value = initialValue
  }

  // We then set the height directly, outside of the render loop
  // Trying to set this with state or a textArea will product an incorrect value.
  textArea.style.height = Math.max(initialHeightRef.current, scrollHeight) + "px"
}

/**
 * A custom hook to automatically adjust the height of a textarea element
 * based on its content.
 *
 * @param ref - A React ref object pointing to the textarea element.
 * @param enabled - A boolean flag to enable or disable the autosizing behavior. Defaults to `true`.
 */
export function useAutosizeTextArea(ref: RefObject<HTMLTextAreaElement | null>, enabled: boolean = true) {
  const initialHeightRef = useRef<number>(null)
  const handleInput = useCallback(
    (event: InputEvent) => {
      const textArea = event.currentTarget
      if (textArea instanceof HTMLTextAreaElement) adjustHeight(textArea, initialHeightRef)
    },
    [ref]
  )

  useEffect(() => {
    const textArea = ref?.current

    if (enabled && textArea) {
      adjustHeight(textArea, initialHeightRef)
      textArea?.addEventListener("input", handleInput)
    }

    return () => {
      textArea?.removeEventListener("input", handleInput)
    }
  }, [ref, enabled])
}
